import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Heart,
  MessageCircle,
  Bookmark,
  MapPin,
  Star,
  Search,
  Compass,
  Navigation,
  Globe2,
  CalendarDays,
  Plus,
  MoreHorizontal
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { placesApi, routesApi } from '../services/api';

const HomePage = ({ onTabChange }) => {
  const navigate = useNavigate();
  const { user, isAuthenticated, updateFavorites } = useAuth();
  const { darkMode } = useTheme();
  
  const [feedItems, setFeedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [likedPlaces, setLikedPlaces] = useState(user?.favorites || []);
  const [savedPlaces, setSavedPlaces] = useState([]);
  const [activeFilter, setActiveFilter] = useState('for-you');

  // Premium App Colors
  const bgMain = darkMode ? 'bg-[#0f1115]' : 'bg-[#f4f7f6]';
  const bgCard = darkMode ? 'bg-[#1a1d24]' : 'bg-white';
  const textPrimary = darkMode ? 'text-white' : 'text-[#1a1d24]';
  const textSecondary = darkMode ? 'text-slate-400' : 'text-slate-500';
  const borderLight = darkMode ? 'border-none' : 'border-gray-100';
  const cardShadow = darkMode ? 'shadow-xl shadow-black/40' : 'shadow-lg shadow-teal-900/5';

  const stories = [
    { id: 'all', name: 'Keşfet', image: 'https://images.unsplash.com/photo-1542281286-9e0a16bb7366?auto=format&fit=crop&w=150&q=80' },
    { id: 'historic', name: 'Tarihi', image: 'https://images.unsplash.com/photo-1524231757912-21f4fe3a7200?auto=format&fit=crop&w=150&q=80' },
    { id: 'nature', name: 'Doğa', image: 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?auto=format&fit=crop&w=150&q=80' },
    { id: 'food', name: 'Lezzet', image: 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?auto=format&fit=crop&w=150&q=80' },
    { id: 'routes', name: 'Rotalar', image: 'https://images.unsplash.com/photo-1506744626753-143d2db73b64?auto=format&fit=crop&w=150&q=80' },
  ];

  useEffect(() => {
    const fetchFeed = async () => {
      try {
        const [places, routes] = await Promise.all([
          placesApi.getFeatured(),
          routesApi.getPopular()
        ]);
        
        // Interleave places and routes to create a mixed social feed
        const mixed = [];
        const pArr = Array.isArray(places) ? places : [];
        const rArr = Array.isArray(routes) ? routes : [];
        
        const maxLen = Math.max(pArr.length, rArr.length);
        for(let i=0; i<maxLen; i++) {
          if (pArr[i]) mixed.push({ ...pArr[i], feedType: 'place' });
          if (rArr[i]) mixed.push({ ...rArr[i], feedType: 'route' });
        }
        
        setFeedItems(mixed);
      } catch (error) {
        console.error('Error fetching feed data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchFeed();
  }, []);

  const toggleLike = async (item) => {
    if (!isAuthenticated) return navigate('/login');
    const id = item.id + item.feedType; // unique string for mock
    setLikedPlaces(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const toggleSave = (item) => {
    if (!isAuthenticated) return navigate('/login');
    const id = item.id + item.feedType;
    setSavedPlaces(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const isLikedItem = (item) => likedPlaces.includes(item.id + item.feedType);
  const isSavedItem = (item) => savedPlaces.includes(item.id + item.feedType);

  return (
    <div className={`min-h-full ${bgMain} pb-24 font-sans flex justify-center`}>
      <div className={`w-full max-w-3xl sm:border-x ${borderLight} ${bgCard} min-h-screen`}>
        
        {/* PREMIUM TOP HEADER SECTION */}
        <div className="px-4 pt-6 pb-2">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className={`text-2xl sm:text-3xl font-extrabold tracking-tight ${textPrimary}`}>Keşfet</h1>
              <p className={`text-sm sm:text-base font-medium ${darkMode ? 'text-teal-400' : 'text-teal-600'}`}>Giresun'un en iyi gezi hikayeleri</p>
            </div>
          </div>

          {/* Practical Search Input */}
          <div className={`flex items-center px-4 h-14 ${bgCard} rounded-2xl border ${borderLight} shadow-sm focus-within:ring-2 focus-within:ring-teal-500/50 transition-all cursor-pointer group`} onClick={() => onTabChange && onTabChange('explore')}>
             <Search className={`w-5 h-5 ${textSecondary} group-hover:text-teal-500 transition-colors`} />
             <input 
               type="text" 
               placeholder="Mekan, rota veya deneyim ara..." 
               className="w-full bg-transparent border-none focus:ring-0 text-sm font-medium pl-3 outline-none text-current" 
               readOnly
             />
          </div>
        </div>

        {/* PREMIUM STORIES TRAY */}
        <div className="pt-4 pb-2">
          <div className="flex gap-4 overflow-x-auto px-4 scrollbar-hide snap-x">
            {/* User "Add Story" */}
            <div className="flex flex-col items-center gap-1.5 cursor-pointer flex-shrink-0 snap-start group">
              <div className={`relative w-[72px] h-[72px] rounded-full overflow-hidden border-2 ${darkMode ? 'border-slate-800' : 'border-white'} p-0.5 shadow-sm`}>
                <img 
                  src={user?.avatar || `https://ui-avatars.com/api/?name=${user?.name || 'Ben'}&background=random`} 
                  alt="My Story" 
                  className="w-full h-full rounded-full object-cover group-hover:scale-105 transition-transform"
                />
                <div className={`absolute bottom-0 right-0 w-6 h-6 bg-teal-500 rounded-full border-2 ${darkMode ? 'border-slate-800' : 'border-white'} flex items-center justify-center`}>
                  <Plus className="w-3.5 h-3.5 text-white" />
                </div>
              </div>
              <span className={`text-[11px] font-bold ${textPrimary} truncate w-[72px] text-center`}>Hikayen</span>
            </div>

            {/* Categories */}
            {stories.map(story => (
              <div key={story.id} className="flex flex-col items-center gap-1.5 cursor-pointer flex-shrink-0 snap-start group">
                <div className="w-[76px] h-[76px] rounded-full bg-gradient-to-tr from-teal-400 via-emerald-500 to-green-500 p-[2.5px] shadow-sm group-hover:shadow-md transition-shadow">
                  <div className={`w-full h-full rounded-full overflow-hidden border-2 ${darkMode ? 'border-[#0f1115]' : 'border-white'} bg-white`}>
                    <img src={story.image} alt={story.name} className="w-full h-full object-cover" />
                  </div>
                </div>
                <span className={`text-[11px] font-bold ${textPrimary} truncate w-[72px] text-center`}>{story.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* FEED FILTERS */}
        <div className="px-5 mt-6 mb-4 flex gap-6 border-b border-gray-200 dark:border-gray-800/50">
           <button 
             onClick={() => setActiveFilter('for-you')}
             className={`pb-3 text-sm font-bold transition-all relative ${activeFilter === 'for-you' ? textPrimary : textSecondary}`}
           >
             Sana Özel
             {activeFilter === 'for-you' && <div className="absolute bottom-0 left-0 w-full h-[3px] bg-teal-500 rounded-t-full"></div>}
           </button>
           <button 
             onClick={() => setActiveFilter('routes')}
             className={`pb-3 text-sm font-bold transition-all relative ${activeFilter === 'routes' ? textPrimary : textSecondary}`}
           >
             Trend Rotalar
             {activeFilter === 'routes' && <div className="absolute bottom-0 left-0 w-full h-[3px] bg-teal-500 rounded-t-full"></div>}
           </button>
           <button 
             onClick={() => setActiveFilter('new')}
             className={`pb-3 text-sm font-bold transition-all relative ${activeFilter === 'new' ? textPrimary : textSecondary}`}
           >
             Son Keşifler
             {activeFilter === 'new' && <div className="absolute bottom-0 left-0 w-full h-[3px] bg-teal-500 rounded-t-full"></div>}
           </button>
        </div>

        {/* FEED CONTENT */}
        <div className="flex flex-col gap-6 px-4">
          {loading ? (
            <div className="p-10 flex justify-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-teal-500"></div>
            </div>
          ) : feedItems.length > 0 ? (
            feedItems.filter(item => activeFilter === 'routes' ? item.feedType === 'route' : true).map((item, index) => {
              const baseLikes = item.id ? item.id.toString().length * 87 + 120 + index * 42 : 250; 
              const itemNameSafe = item.name || item.title || 'Anonim';
              const itemChar = itemNameSafe.charAt ? itemNameSafe.charAt(0) : 'A';
              
              return (
                <div key={`${item.id || index}-${item.feedType}`} className={`${bgCard} rounded-3xl overflow-hidden border ${borderLight} ${cardShadow} flex flex-col`}>
                  
                  {/* Post Header */}
                  <div className="flex items-center justify-between px-3 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full overflow-hidden bg-gradient-to-tr from-teal-400 to-blue-500 p-[1.5px]">
                        <div className={`w-full h-full rounded-full border ${bgCard} overflow-hidden`}>
                          <img 
                            src={`https://ui-avatars.com/api/?name=${itemChar}&background=random`} 
                            alt="Author" 
                            className="w-full h-full object-cover"
                          />
                        </div>
                      </div>
                      <div className="flex flex-col">
                        <span className={`text-sm font-semibold ${textPrimary} leading-tight`}>
                          giresunkent_rehberi
                          <span className="text-blue-500 ml-1 text-xs">✓</span>
                        </span>
                        <span className={`text-xs ${textSecondary} leading-tight`}>{item.location || 'Giresun Merkez'}</span>
                      </div>
                    </div>
                    <button className={`${textSecondary} hover:${textPrimary}`}>
                      <MoreHorizontal className="w-5 h-5" />
                    </button>
                  </div>

                  {/* Visual Header / Image Box */}
                  <div className="relative aspect-[4/3] w-full group cursor-pointer" onDoubleClick={() => toggleLike(item)}>
                     <img 
                       src={item.image || 'https://images.unsplash.com/photo-1596394516093-501ba68a0ba6?w=800&q=80'} 
                       alt={itemNameSafe} 
                       className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                     />
                     
                     {/* Gradient Overlay for Readability */}
                     <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/10 pointer-events-none"></div>

                     {/* Glassmorphism Badge */}
                     <div className="absolute top-4 left-4 bg-black/30 backdrop-blur-md px-3 py-1.5 rounded-full border border-white/20 text-white text-[11px] font-bold shadow-lg flex items-center gap-1.5">
                        {item.feedType === 'route' ? <Navigation className="w-3.5 h-3.5 text-teal-300" /> : <Star className="w-3.5 h-3.5 text-yellow-400 fill-current" />}
                        {item.feedType === 'route' ? 'Popüler Rota' : 'Öne Çıkan Mekan'}
                     </div>

                     {/* Authorship tag floating on image */}
                     <div className="absolute top-4 right-4 flex items-center gap-2 bg-black/40 backdrop-blur-md px-2 py-1 rounded-full border border-white/20">
                        <img 
                          src={`https://ui-avatars.com/api/?name=${itemChar}&background=random`}
                          className="w-6 h-6 rounded-full border border-white/50"
                        />
                        <span className="text-white text-xs font-semibold pr-1">rehberiniz_gk</span>
                     </div>

                     {/* Bottom Floating Info */}
                     <div className="absolute bottom-4 left-4 right-4 flex justify-between items-end">
                        <div className="text-white">
                           <h2 className="text-xl md:text-2xl font-extrabold leading-tight mb-1 drop-shadow-lg">{itemNameSafe}</h2>
                           <div className="flex items-center gap-1.5 text-sm font-medium opacity-90">
                              <MapPin className="w-4 h-4" /> {item.location || 'Giresun Merkez'}
                           </div>
                        </div>
                     </div>
                  </div>
                  
                  {/* Interactive Content Side */}
                  <div className="p-5 flex flex-col">
                     {/* Stats & Brief */}
                     {item.feedType === 'route' && (
                       <div className="flex gap-4 mb-3 p-3 bg-teal-50 dark:bg-teal-900/10 rounded-2xl text-teal-700 dark:text-teal-400 text-sm font-bold">
                          <div className="flex items-center gap-1"><Compass className="w-4 h-4"/> {item.places_count || 4} Durak</div>
                          <div className="flex items-center gap-1"><CalendarDays className="w-4 h-4"/> {item.duration || 'Günübirlik'}</div>
                       </div>
                     )}

                     <p className={`text-[15px] ${textSecondary} line-clamp-2 leading-relaxed font-medium mb-5`}>
                        {item.description || (item.feedType === 'route' 
                           ? "Hafta sonu arkadaşlarınızla deneyimleyebileceğiniz muazzam bir keşif rotası haritası. Mutlaka kaydedin."
                           : "Eşsiz lezzetleri ve harika atmosferiyle Giresun'da mutlaka deneyimlemeniz gereken nadide yerlerden biri.")}
                     </p>
                     
                     <div className="flex items-center justify-between pt-1">
                        {/* Interactive Social Actions (Like/Comment) */}
                        <div className="flex items-center gap-2">
                           <button 
                             onClick={() => toggleLike(item)}
                             className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all font-bold text-sm ${
                               isLikedItem(item) 
                                 ? 'bg-red-50 dark:bg-red-900/20 text-red-500' 
                                 : `bg-gray-100 dark:bg-gray-800 ${textPrimary} hover:bg-gray-200 dark:hover:bg-gray-700`
                             }`}
                           >
                             <Heart className={`w-5 h-5 ${isLikedItem(item) ? 'fill-current' : ''}`} />
                             {(baseLikes + (isLikedItem(item) ? 1 : 0)).toLocaleString()}
                           </button>

                           <button className={`w-10 h-10 rounded-full flex items-center justify-center bg-gray-100 dark:bg-gray-800 ${textPrimary} hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors`}>
                             <MessageCircle className="w-5 h-5" />
                           </button>
                        </div>

                        {/* Save / Book */}
                        <button 
                          onClick={() => toggleSave(item)}
                          className={`flex items-center gap-2 px-4 py-2 rounded-full font-bold text-sm transition-all ${
                            isSavedItem(item)
                              ? 'bg-teal-500 text-white shadow-md shadow-teal-500/30'
                              : 'bg-teal-50 dark:bg-teal-900/20 text-teal-600 dark:text-teal-400 hover:bg-teal-100 dark:hover:bg-teal-900/40'
                          }`}
                        >
                          <Bookmark className={`w-4 h-4 ${isSavedItem(item) ? 'fill-current' : ''}`} />
                          {isSavedItem(item) ? 'Kaydedildi' : 'Listeme Ekle'}
                        </button>
                     </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="p-10 text-center text-gray-500">Gösterilecek içerik bulunamadı.</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HomePage;
